import {
  AdsClickOutlined,
  AutoAwesomeOutlined,
  BarChart,
  Delete,
  GraphicEqOutlined,
  HttpOutlined,
  LocalOfferOutlined,
  MoreVert,
  PlayArrow,
  PlaylistAddOutlined,
  SearchOutlined,
  SettingsApplications,
  Stop,
  Assignment,
  InfoOutlined,
  SyncOutlined,
  TimelineOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import { Bot, Image as LucideImage } from "lucide-react";
import { SvgIcon } from "@mui/material";
import { LIST_ACTION_TYPE_ADD_ROWS, LIST_ACTIONS, ListAction } from "../../constants";
import { INTEGRATION_LOGOS } from "../integrations/constants";

// Wrapper to make Lucide Bot icon compatible with MUI
const BotIcon = (props: any) => (
  <SvgIcon {...props}>
    <Bot />
  </SvgIcon>
);

// Wrapper to make Lucide Image icon compatible with MUI
const ImageIcon = (props: any) => (
  <SvgIcon {...props}>
    <LucideImage />
  </SvgIcon>
);

// MUI icon mapping for list actions
const MUI_ICONS: Record<string, React.ElementType> = {
  SmartToy: BotIcon,
  Timeline: TimelineOutlined,
  Http: HttpOutlined,
  PlaylistAdd: PlaylistAddOutlined,
  AutoAwesome: AutoAwesomeOutlined,
  Image: ImageIcon,
  GraphicEq: GraphicEqOutlined,
  Search: SearchOutlined,
  Sync: SyncOutlined,
  AdsClick: AdsClickOutlined,
  BarChart,
  LocalOffer: LocalOfferOutlined,
};

// Helper component to render action icon (MUI icon or integration logo)
const ActionIcon: React.FC<{ action: ListAction; size?: number }> = ({ action, size = 18 }) => {
  if (action.icon.type === "logo") {
    const logoSrc = INTEGRATION_LOGOS[action.icon.name];
    if (logoSrc) {
      return (
        <Avatar
          src={logoSrc}
          alt={action.title}
          sx={{ width: size, height: size }}
        />
      );
    }
  }

  // MUI icon
  const IconComponent = MUI_ICONS[action.icon.name];
  if (IconComponent) {
    return <IconComponent sx={{ fontSize: size }} />;
  }

  return null;
};

// Helper to get action type info from LIST_ACTIONS
const getActionTypeInfo = (actionType: string) => {
  const action = LIST_ACTIONS.find((a) => a.type === actionType);
  if (action) {
    return {
      label: action.title,
      description: action.description || "Performs an action on your list data.",
      action,
    };
  }
  return {
    label: actionType,
    description: "Performs an action on your list data.",
    action: null,
  };
};

export default ({
  actions,
  onRunAction,
  onEditAction,
  onStopAction,
  onDeleteAction,
  onViewSummary,
  onViewMetrics,
}: {
  actions: any[];
  onRunAction: (action: any) => void;
  onEditAction: (action: any) => void;
  onStopAction: (action: any) => void;
  onDeleteAction: (action: any) => void;
  onViewSummary: (action: any) => void;
  onViewMetrics: (action: any) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAction, setSelectedAction] = useState<any>(null);

  const handleMenuOpen = (event: any, action: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedAction(action);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAction(null);
  };

  const handleRunAction = () => {
    if (selectedAction) {
      onRunAction(selectedAction);
    }
    handleMenuClose();
  };

  const handleStopAction = () => {
    if (selectedAction) {
      onStopAction(selectedAction);
    }
    handleMenuClose();
  };

  const handleEditAction = () => {
    if (selectedAction) {
      onEditAction(selectedAction);
    }
    handleMenuClose();
  };

  const handleDeleteAction = () => {
    if (selectedAction) {
      onDeleteAction(selectedAction);
    }
    handleMenuClose();
  };

  const handleViewSummary = () => {
    if (selectedAction) {
      onViewSummary(selectedAction);
    }
    handleMenuClose();
  };

  const handleViewMetrics = () => {
    if (selectedAction) {
      onViewMetrics(selectedAction);
    }
    handleMenuClose();
  };

  if (!actions || actions.length === 0) {
    return <></>;
  }

  return (
    <Box sx={{ my: 3 }}>
      <Typography sx={{ mb: 1 }} variant="h6">
        Table Actions
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Updated Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map((action) => (
              <TableRow key={action.id}>
                <TableCell>{action.name}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {(() => {
                      const actionInfo = getActionTypeInfo(action.actionType || action.type);
                      return (
                        <>
                          <Chip
                            icon={actionInfo.action ? <ActionIcon action={actionInfo.action} /> : undefined}
                            label={actionInfo.label}
                            size="small"
                            color="primary"
                            variant="filled"
                          />
                          <Tooltip
                            title={actionInfo.description}
                            placement="right"
                            arrow
                          >
                            <InfoOutlined
                              sx={{
                                fontSize: 16,
                                color: "text.secondary",
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        </>
                      );
                    })()}
                  </Box>
                </TableCell>
                <TableCell>
                  {action.loading ? (
                    <Chip
                      icon={<CircularProgress size={15} />}
                      label="Working"
                    />
                  ) : (
                    <Chip
                      label={action.researchSummary ? "Completed" : "Ready"}
                      size="small"
                      color={action.researchSummary ? "primary" : "warning"}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {action.created_date
                    ? moment(action.created_date).format(
                        "MMMM Do YYYY, h:mm:ss a"
                      )
                    : "-"}
                </TableCell>
                <TableCell>
                  {action.updated_date
                    ? moment(action.updated_date).format(
                        "MMMM Do YYYY, h:mm:ss a"
                      )
                    : "-"}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, action)}
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            sx={{
              display:
                selectedAction && selectedAction.loading ? "flex" : "none",
            }}
            onClick={handleStopAction}
          >
            <Stop sx={{ mr: 1 }} />
            Stop Action
          </MenuItem>
          <MenuItem
            sx={{
              display:
                selectedAction && selectedAction.loading ? "flex" : "none",
            }}
            onClick={handleViewMetrics}
          >
            <BarChart sx={{ mr: 1 }} />
            View Progress
          </MenuItem>
          <MenuItem
            sx={{
              display:
                selectedAction &&
                selectedAction.researchSummary &&
                selectedAction.type === LIST_ACTION_TYPE_ADD_ROWS
                  ? "flex" : "none",
            }}
            onClick={handleViewSummary}
          >
            <Assignment sx={{ mr: 1 }} />
            View Summary
          </MenuItem>
          <MenuItem
            sx={{
              display:
                selectedAction && selectedAction.loading ? "none" : "flex",
            }}
            onClick={handleRunAction}
          >
            <PlayArrow sx={{ mr: 1 }} />
            Run Action
          </MenuItem>
          <MenuItem onClick={handleEditAction}>
            <SettingsApplications sx={{ mr: 1 }} />
            Edit Action
          </MenuItem>
          <MenuItem onClick={handleDeleteAction}>
            <Delete sx={{ mr: 1 }} />
            Delete Action
          </MenuItem>
        </Menu>
      </TableContainer>
    </Box>
  );
};
